import { NgModule, ModuleWithProviders } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";
import { RouterModule, Routes } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FileUploadModule } from "ng2-file-upload";
import { QuillModule } from "ngx-quill";
import { ClipboardModule } from "ngx-clipboard";
import {NgPipesModule} from "ngx-pipes";
import { NgSelectModule } from "@ng-select/ng-select";

// modules
import { NotificationsModule } from "#notifications/notifications.module";
import { ProfileModule } from "#core/profile/profile.module";

// components
import * as fromComponents from "./components";

// containers
import { AssignCartContentBuilderComponent } from "#core/components/content-library/assign-cart/containers/assign-cart-content-builder/assign-cart-content-builder.component";
import { AssignCartSidePanelComponent } from "#core/components/content-library/assign-cart/containers/assign-cart-side-panel/assign-cart-side-panel.component";
import { AvenueAssetsComponent } from "#core/components/content-library/assign-cart/containers/avenue-assets/avenue-assets.component";
import { AvenueAssetDialogComponent } from "#core/components/content-library/assign-cart/containers/avenue-asset-dialog/avenue-asset-dialog.component";

// components
import { AssignCartTabComponent } from "#core/components/content-library/assign-cart/components/assign-cart-tab/assign-cart-tab.component";
import { ActivityBuilderComponent } from "#core/components/content-library/assign-cart/components/activity-builder/activity-builder.component";
import { AvenueAssetFormComponent } from "#core/components/content-library/assign-cart/components/avenue-asset-form/avenue-asset-form.component";
import { AvenueAssetItemComponent } from "#core/components/content-library/assign-cart/components/avenue-asset-item/avenue-asset-item.component";
import { AvenueAssetRecorderComponent } from "#core/components/content-library/assign-cart/components/avenue-asset-recorder/avenue-asset-recorder.component";
import { AvenueAssetListComponent } from "#core/components/content-library/assign-cart/components/avenue-asset-list/avenue-asset-list.component";
import { AvenueActivityFormComponent } from "#core/components/content-library/assign-cart/components/avenue-form/avenue-form.component";
import { CustomActivityFormComponent } from "#core/components/content-library/assign-cart/components/custom-activity-form/custom-activity-form.component";
import { LessonBuilderFormComponent } from "#core/components/content-library/assign-cart/components/lesson-builder-form/lesson-builder-form.component";
import { LessonFormFieldsComponent } from "#core/components/content-library/assign-cart/components/lesson-form-fields/lesson-form-fields.component";
import { ClearSelectedAssignablesDialogComponent } from "#core/components/content-library/assign-cart/containers/assign-cart-side-panel/assign-cart-side-panel.component";
import { WarningDialogComponent } from "#core/components/content-library/assign-cart/components/warning-dialog/warning-dialog.component";

// Dialog Components
import { ActivityPreviewDialogComponent } from "#core/components/dialog-boxes/activity-preview-dialog/activity-preview-dialog.component";
import { TaskCategoryDialogComponent } from "#core/components/content-library/assignable-expanded/task-category-dialog/task-category-dialog.component";
import { RosterCourseLanguageFormComponent } from "#core/teacher-classes/components/roster-course-language-form/roster-course-language-form.component";
import { SearchInfoDialogComponent } from "#core/components/dialog-boxes/search-info-dialog/search-info-dialog.component";
import { FeedbackDialogComponent } from "#components/dialog-boxes/feedback-dialog/feedback-dialog.component";
import { AssignmentInfoDialogComponent } from "#components/dialog-boxes/assignment-info-dialog/assignment-info-dialog.component";
import { GenericDialogComponent } from "#core/components/dialog-boxes/generic-dialog/generic-dialog.component";
import { AssignablePreviewDetailsComponent } from "#components/content-library/assignable-preview-details/assignable-preview-details.component";
import { PreviewAssetDialogComponent } from "#components/dialog-boxes/preview-asset-dialog/preview-asset-dialog.component";

// services
import * as fromServices from "./services";

// helpers
import * as fromHelpers from "./helpers";

// directives
import * as fromDirectives from "./directives";

// pipes
import * as fromPipes from "./pipes";
import { TimerIndicatorPipe } from "./pipes/timer-indicator.pipe";

// interceptors
import * as fromInterceptors from "./interceptors";

import { AddToCartBtnComponent } from "#components/add-to-cart-btn/add-to-cart-btn.component";
import { IdleCheckerDialogComponent } from "#components/dialog-boxes/idle-checker-dialog/idle-checker-dialog.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { DateRangesFilterMenuComponent } from "./components/layout-components/date-ranges-filter-menu/date-ranges-filter-menu.component";
import { OwlDateTimeModule } from "@danielmoncada/angular-datetime-picker";
import { AvenueEvalStudentVideoComponent } from "./components/assignments/activities/performance-activity-components/avenue-eval-student-video/avenue-eval-student-video.component";
import { PageBannerComponent } from "./components/layout-components/page-banner/page-banner.component";
import { ReadycheckReportDialogComponent } from "./components/dialog-boxes/readycheck-report-dialog/readycheck-report-dialog.component";
import { ValueSelectorComponent } from "./components/layout-components/value-selector/value-selector.component";
import { AssessmentDirectionsComponent } from "./components/activity-directions/assessment-directions/assessment-directions.component";
import { BundleDirectionsComponent } from "./components/activity-directions/bundle-directions/bundle-directions.component";
import { BundleSkillsComponent } from "./components/activity-directions/bundle-skills/bundle-skills.component";
import { ReadyCheckDirectionsComponent } from "#components/activity-directions/ready-check-directions/ready-check-directions.component";
import { CustomActivityDirectionsComponent } from "#components/activity-directions/custom-activity-directions/custom-activity-directions.component";
import { CogTutorDirectionsComponent } from "#components/activity-directions/cog-tutor-directions/cog-tutor-directions.component";
import { PerformanceDirectionsComponent } from "#components/activity-directions/performance-directions/performance-directions.component";
import { PrintErrorModule } from "./print-error/print-error.module";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { MatDialogModule } from "@angular/material/dialog";
import { MatCardModule } from "@angular/material/card";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatListModule } from "@angular/material/list";
import { MatTabsModule } from "@angular/material/tabs";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatDividerModule } from "@angular/material/divider";
import { MatRadioModule } from "@angular/material/radio";
import { MatBadgeModule } from "@angular/material/badge";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonToggleModule } from "@angular/material/button-toggle";

import { NgAisRootModule, NgAisInstantSearch } from "@carnegie-learning/angular-instantsearch";
import {
    DemoTeacherStudentToggleComponent,
    DemoTeacherStudentToggleDialog,
} from "#components/layout-components/demo-teacher-student-toggle/demo-teacher-student-toggle.component";
import { MultipleValuesSelectorComponent } from "./components/layout-components/multiple-values-selector/multiple-values-selector.component";
import { MathExtLinkActivityComponent } from "./components/assignments/activities/math-ext-link-activity/math-ext-link-activity.component";
import { StatusTabsComponent } from "./components/layout-components/status-tabs/status-tabs.component";
import { StatusLaunchButtonsComponent } from "./components/layout-components/status-launch-buttons/status-launch-buttons.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTableModule } from "@angular/material/table";
import { MatSliderModule } from "@angular/material/slider";
import { MatStepperModule } from "@angular/material/stepper";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatInputModule } from "@angular/material/input";
import { MatMomentDateModule } from "@angular/material-moment-adapter";
import { MatNativeDateModule } from "@angular/material/core";
import { TrimInputDirective } from "./directives/trim-input/trim-input.directive";

export const ROUTES: Routes = [
    {
        path: "support",
        component: fromComponents.BackgroundComponent,
        children: [
            { path: "form", component: fromComponents.SupportComponent },
            { path: "**", redirectTo: "form", pathMatch: "prefix" },
        ],
    },
];

@NgModule({
    imports: [
        CommonModule,
        MatBadgeModule,
        MatButtonToggleModule,
        MatCardModule,
        MatDatepickerModule,
        MatDialogModule,
        MatDividerModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatListModule,
        MatMenuModule,
        MatRadioModule,
        MatSelectModule,
        MatTabsModule,
        MatToolbarModule,
        FlexLayoutModule,
        FormsModule,
        ReactiveFormsModule,
        ProfileModule,
        RouterModule.forChild(ROUTES),
        NotificationsModule.forRoot(),
        PrintErrorModule,
        FileUploadModule,
        QuillModule,
        TrimInputDirective,
        NgSelectModule,
        ClipboardModule,
        NgPipesModule,
        DragDropModule,
        OwlDateTimeModule,
        NgAisRootModule,
        MatTooltipModule,
        MatSlideToggleModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatSliderModule,
        MatStepperModule,
        MatProgressBarModule,
        MatButtonModule,
        MatCheckboxModule,
        MatInputModule,
        MatMomentDateModule,
        MatNativeDateModule,
    ],
    declarations: [
        SearchInfoDialogComponent,
        ActivityPreviewDialogComponent,
        TaskCategoryDialogComponent,
        AssignablePreviewDetailsComponent,
        PreviewAssetDialogComponent,
        RosterCourseLanguageFormComponent,
        ...fromComponents.components,
        ...fromDirectives.directives,
        ...fromPipes.pipes,
        TimerIndicatorPipe,
        FeedbackDialogComponent,
        AssignmentInfoDialogComponent,
        GenericDialogComponent,
        AddToCartBtnComponent,
        IdleCheckerDialogComponent,
        AssignCartContentBuilderComponent,
        AssignCartSidePanelComponent,
        AvenueAssetsComponent,
        AvenueAssetDialogComponent,
        AssignCartTabComponent,
        ActivityBuilderComponent,
        AvenueAssetFormComponent,
        AvenueAssetItemComponent,
        AvenueAssetRecorderComponent,
        AvenueActivityFormComponent,
        CustomActivityFormComponent,
        LessonBuilderFormComponent,
        LessonFormFieldsComponent,
        ClearSelectedAssignablesDialogComponent,
        WarningDialogComponent,
        AvenueAssetListComponent,
        DateRangesFilterMenuComponent,
        AvenueEvalStudentVideoComponent,
        PageBannerComponent,
        ReadycheckReportDialogComponent,
        ValueSelectorComponent,
        AssessmentDirectionsComponent,
        BundleDirectionsComponent,
        BundleSkillsComponent,
        ReadyCheckDirectionsComponent,
        CustomActivityDirectionsComponent,
        CogTutorDirectionsComponent,
        PerformanceDirectionsComponent,
        DemoTeacherStudentToggleComponent,
        DemoTeacherStudentToggleDialog,
        MultipleValuesSelectorComponent,
        MathExtLinkActivityComponent,
        SearchInfoDialogComponent,
        TaskCategoryDialogComponent,
        RosterCourseLanguageFormComponent,
        ActivityPreviewDialogComponent,
        AssignablePreviewDetailsComponent,
        PreviewAssetDialogComponent,
        FeedbackDialogComponent,
        AssignmentInfoDialogComponent,
        GenericDialogComponent,
        AddToCartBtnComponent,
        IdleCheckerDialogComponent,
        ClearSelectedAssignablesDialogComponent,
        WarningDialogComponent,
        AvenueAssetDialogComponent,
        StatusTabsComponent,
        StatusLaunchButtonsComponent,
    ],
    exports: [
        NotificationsModule,
        PrintErrorModule,
        FileUploadModule,
        ProfileModule,
        RosterCourseLanguageFormComponent,
        ...fromComponents.components,
        ...fromDirectives.directives,
        ...fromPipes.pipes,
        FeedbackDialogComponent,
        AssignmentInfoDialogComponent,
        AddToCartBtnComponent,
        IdleCheckerDialogComponent,
        AssignablePreviewDetailsComponent,
        AssignCartContentBuilderComponent,
        AssignCartSidePanelComponent,
        AvenueAssetsComponent,
        AvenueAssetDialogComponent,
        AssignCartTabComponent,
        ActivityBuilderComponent,
        AvenueAssetFormComponent,
        AvenueAssetItemComponent,
        AvenueAssetRecorderComponent,
        AvenueActivityFormComponent,
        CustomActivityFormComponent,
        LessonBuilderFormComponent,
        LessonFormFieldsComponent,
        AvenueAssetListComponent,
        PageBannerComponent,
        DemoTeacherStudentToggleComponent,
        DemoTeacherStudentToggleDialog,
        MathExtLinkActivityComponent,
    ],
})
export class CoreModule {
    // to prevent duplicate instances of the CoreModule
    static forRoot(): ModuleWithProviders<CoreModule> {
        return {
            ngModule: CoreModule,
            providers: [
                ...fromServices.services,
                ...fromHelpers.helpers,
                ...fromInterceptors.interceptors,
                NgAisInstantSearch,
            ],
        };
    }
}
